import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';

import { Alert, Dialog } from '@mui/material';
import { useMst } from '../../../models/rootModel';
import { useAnalytics } from '../../../utils/analytics';
import { sendGTMEvent } from '../../../utils/googleTagManager';
import { isMobileAppWebView } from '../../../utils/utilsFunctions';
import { LogoWithName } from '../../common/LogoWithName';
import { MobilePaymentSummary } from '../../common/PaymentSummary/MobilePaymentSummary';
import { PaymentSummary } from '../../common/PaymentSummary/PaymentSummary';
import { TermsOfServicePopUp } from '../../common/TermsOfServicePopUp';
import { BaseStepV2 } from '../../common/v2/BaseStepV2';
import { StepV2ConfirmButton } from '../../common/v2/StepV2ConfirmButton';
import { StepV2MainContent } from '../../common/v2/StepV2MainContent';
import { ContentBoxWrapper, MainContentBox } from '../../common/v2/Box/Box.style';
import { SpanLink } from '../../styledComponent/SignUpComponents.style';
import { PaymentSecurityBox } from '../PaymentStep/PaymentSecurityBox';
import { PaymentMethod } from '../PaymentStep/paymentStep.types';
import { planDataExtractor } from '../PaymentStep/planDataExtractor';
import { SignUpErrorPopUp } from '../SignUp/SignUpErrorPopUp';
import {
  CheckoutFormContentBoxWrapper,
  SignupWithPaymentSummaryCol,
  SignupWithPaymentTwoSidedLayout,
  SubmitButtonWrapper,
  TermsLabelWrapper,
} from './SignupWithPayment.style';
import { SignupWithPaymentStepProps } from './SignupWithPayment.type';
import { PaymentForm } from './components/PaymentForm';
import { SignupForm } from './components/SignupForm';
import { usePayment } from './hooks/usePayment.hook';
import { useSignup } from './hooks/useSignup.hook';
import { usePaymentRequest } from './hooks/usePaymentRequest.hook';
import { ErrorType, recordError } from '../../../models/apiWrappers/api/exceptions';
import { PaymentRequestPaymentMethodEvent } from '@stripe/stripe-js';
import { ContentCol } from '../../common/v2/TwoSidedLayout/TwoSidedLayout.style';
import { SuggestPlan } from '../../common/PaymentSummary/SuggestPlan';
import { QuoraEventName } from '../../../models/types/useQuora.types';

const useMockedSignupData = process.env.REACT_APP_USE_MOCKED_SIGNUP_DATA === '1';
const paymentMethod = PaymentMethod.CreditCard;

export const SignupWithPayment: React.FC<SignupWithPaymentStepProps> = observer((props) => {
  const { inputFields, isActive, showPaymentSummary, nextStep, stepName } = props;

  const analytics = useAnalytics();
  const { UserPayment, UserDiscoveryResponse } = useMst();

  const plan = UserPayment.getChoosenPlan();
  const currentPlan = planDataExtractor(plan);
  const discount = toJS(UserPayment.discount);

  const [isTermsPopUpOpen, setIsTermsPopUpOpen] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const originalPlanInterval = useMemo(() => plan?.interval, []);

  const handleExpressPaymentFlow = async (ev: PaymentRequestPaymentMethodEvent) => {
    signupAndPay(true, ev);
  };
  const handleCreditCardPaymentFlow = async () => {
    analytics.track('submit signup with payment page', {
      stepName,
      planTitle: currentPlan.sanitizedPlanTitle,
      segment: UserDiscoveryResponse.segment,
      sku: UserPayment?.chosenPlanPosition && UserPayment.stripePlans[UserPayment.chosenPlanPosition].id,
    });
    signupAndPay(false, undefined);
  };

  const signupAndPay = async (
    isExpressPaymentFlow: boolean,
    paymentRequestEvent?: PaymentRequestPaymentMethodEvent
  ) => {
    try {
      if (!validateSignupForm()) {
        return;
      }

      if (!isExpressPaymentFlow && !validateCreditCardForm()) {
        handlePotentialExpressPaymentFail(paymentRequestEvent);
        return;
      }

      const signupResult = await signUp();

      if (!signupResult) {
        handlePotentialExpressPaymentFail(paymentRequestEvent);
        return;
      }

      const paymentResult = await processPayment(paymentRequestEvent);

      if (paymentResult.error) {
        handlePotentialExpressPaymentFail(paymentRequestEvent);
        setPaymentErrorAlertText(paymentResult.customErrorMessage ?? 'An error occurred');
        return;
      }

      UserDiscoveryResponse.sendQuoraConversionEvent({
        eventName: QuoraEventName.Purchase,
        value: currentPlan.sanitizedAmount / 100,
      });

      nextStep();
    } catch (error) {
      recordError(ErrorType.HANDLED, error);
      handlePotentialExpressPaymentFail(paymentRequestEvent);
      setPaymentErrorAlertText('An error occurred');
    }
  };

  const openTerms = () => {
    if (!isMobileAppWebView) {
      window.open(`${process.env.REACT_APP_WEBSITE}/terms-of-use/`, '_blank');
      return;
    }
  };
  const openPrivacy = () => {
    if (!isMobileAppWebView) {
      window.open(`${process.env.REACT_APP_WEBSITE}/privacy/`, '_blank');
      return;
    }
  };

  const canShowPaymentSummary = Boolean(showPaymentSummary && plan);

  const {
    signUpInputError,
    signUpValues,
    handleSignupInputChange,
    handleSignupPhoneChange,
    isSignupErrorPopUpOpen,
    onSignupErrorClosePopup,
    signUp,
    setIsSignupErrorPopUpOpen,
    signupServerErrorMessage,
    validateSignupForm,
    isSignupBeingProcessed,
    isUserSignedUp,
  } = useSignup({ useMockedSignupData, stepProps: props });

  const {
    processPayment,
    isPaymentBeingProcessed,
    paymentErrorAlertText,
    setPaymentErrorAlertText,
    isAnyCredidCardFieldChanged,
    cardNumberState,
    setCardNumberState,
    cardExpState,
    setCardExpState,
    cardCvcState,
    setCardCvcState,
    validateCreditCardForm,
    recordCreditCardFieldChange,
    handlePotentialExpressPaymentFail,
  } = usePayment({
    currentPlan,
    discount,
    paymentMethod,
    stepProps: props,
  });

  const { paymentRequest } = usePaymentRequest({ currentPlan, discount, paymentAction: handleExpressPaymentFlow });

  useEffect(() => {
    if (isActive) {
      analytics.track('view signup with payment page', {
        stepName,
        planTitle: currentPlan.sanitizedPlanTitle,
        segment: UserDiscoveryResponse.segment,
        sku: UserPayment?.chosenPlanPosition && UserPayment.stripePlans[UserPayment.chosenPlanPosition].id,
      });
      sendGTMEvent({
        event: 'Signup_Enter_Details',
      });
      window.scrollTo(0, 0);
    }
  }, [
    isActive,
    analytics,
    currentPlan.sanitizedPlanTitle,
    UserDiscoveryResponse.segment,
    UserPayment.chosenPlanPosition,
    UserPayment.stripePlans,
    stepName,
  ]);

  if (!isActive) {
    return null;
  }

  const planName = `${currentPlan.sanitizedDescription}${UserPayment.enableProgram ? '' : ' plan'}`;
  const showOffer = originalPlanInterval !== 'year' && !UserPayment.enableProgram;
  const suggestPlan = originalPlanInterval !== 'year' && !UserPayment.enableProgram;
  const canGoBack = UserPayment.hasPlanSearchParam ? false : props.canGoBack;

  return (
    <BaseStepV2 {...props} addSpacingNeededForMobileButton={false}>
      <TermsOfServicePopUp open={isTermsPopUpOpen} handleClose={() => setIsTermsPopUpOpen(false)} />

      <Dialog open={paymentErrorAlertText.length > 0} onClose={() => setPaymentErrorAlertText('')}>
        <Alert severity="error">{paymentErrorAlertText}</Alert>
      </Dialog>

      {canShowPaymentSummary && (
        <MobilePaymentSummary
          planName={planName}
          planPriceInCents={currentPlan.sanitizedAmount}
          planMonthlyPriceShort={currentPlan.sanitizedMonthlyPriceShort}
          planWeeklyPriceShort={currentPlan.sanitizedWeeklyPriceShort}
          appliedDiscount={discount}
          planCurrency={currentPlan.sanitizedCurrency}
          showCouponForm={true}
          planPriceWithoutBargainShort={currentPlan.sanitizedPriceWithoutBargainShort}
          ShowOffer={showOffer}
        />
      )}

      <SignupWithPaymentTwoSidedLayout showPaymentSummary={Boolean(canShowPaymentSummary)}>
        <ContentCol location="left">
          <CheckoutFormContentBoxWrapper>
            <StepV2MainContent {...props} canGoBack={canGoBack} hideSubmitButton={true}>
              <SignupForm
                inputFields={inputFields}
                signUpValues={signUpValues}
                signUpInputError={signUpInputError}
                phoneChange={handleSignupPhoneChange}
                handleChange={handleSignupInputChange}
                areInputsDisabled={isUserSignedUp}
              />
              <PaymentForm
                onChange={recordCreditCardFieldChange}
                cardNumberState={cardNumberState}
                setCardNumberState={setCardNumberState}
                cardExpState={cardExpState}
                setCardExpState={setCardExpState}
                cardCvcState={cardCvcState}
                setCardCvcState={setCardCvcState}
                paymentRequest={paymentRequest}
                canExpressPaymentBeMade={validateSignupForm}
              />
              <SubmitButtonWrapper>
                <StepV2ConfirmButton
                  {...props}
                  isButtonDisabled={!isAnyCredidCardFieldChanged}
                  isLoading={isSignupBeingProcessed || isPaymentBeingProcessed}
                  onClick={handleCreditCardPaymentFlow}
                />
              </SubmitButtonWrapper>
              <TermsLabelWrapper>
                By continuing, you agree to the Circles <SpanLink onClick={openTerms}>terms of use</SpanLink> and <SpanLink onClick={openPrivacy}>privacy policy</SpanLink>
              </TermsLabelWrapper>

              <PaymentSecurityBox />
            </StepV2MainContent>
          </CheckoutFormContentBoxWrapper>
        </ContentCol>

        <SignupWithPaymentSummaryCol location="right">
          <ContentBoxWrapper offsetVariant={80}>
            <MainContentBox>
              <LogoWithName />
              <PaymentSummary
                planName={currentPlan.sanitizedDescription}
                planPriceInCents={currentPlan.sanitizedAmount}
                planMonthlyPriceShort={currentPlan.sanitizedMonthlyPriceShort}
                planWeeklyPriceShort={currentPlan.sanitizedWeeklyPriceShort}
                appliedDiscount={discount}
                planCurrency={currentPlan.sanitizedCurrency}
                showCouponForm={true}
              />
              {suggestPlan && <SuggestPlan interval="year" />}
            </MainContentBox>
          </ContentBoxWrapper>
        </SignupWithPaymentSummaryCol>
      </SignupWithPaymentTwoSidedLayout>

      <SignUpErrorPopUp
        setIsPopUpOpen={setIsSignupErrorPopUpOpen}
        isPopUpOpen={isSignupErrorPopUpOpen}
        onClosePopUp={onSignupErrorClosePopup}
        serverErrorMessage={signupServerErrorMessage}
      />
    </BaseStepV2>
  );
});
